import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * Carte « OneSignal » du Super Admin (Paramètres → API & IA).
 * Permet d'activer/désactiver la diffusion des notifications push OneSignal
 * et de saisir les identifiants nécessaires. L'intégration n'est chargée
 * côté application QUE si elle est activée et qu'un App ID est renseigné.
 */
export default function OneSignalSettings() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [appId, setAppId] = useState('');
  const [restKey, setRestKey] = useState('');
  const [safariWebId, setSafariWebId] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('zifek')
        .select('onesignal_enabled, onesignal_app_id, onesignal_rest_api_key, onesignal_safari_web_id')
        .limit(1)
        .maybeSingle();
      if (data) {
        setEnabled(!!data.onesignal_enabled);
        setAppId(String(data.onesignal_app_id || ''));
        setRestKey(String(data.onesignal_rest_api_key || ''));
        setSafariWebId(String(data.onesignal_safari_web_id || ''));
      }
    } catch {
      /* garde les valeurs par défaut */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    if (enabled && !appId.trim()) {
      setError('Un App ID est requis pour activer OneSignal.');
      setSaving(false);
      return;
    }
    if (enabled && !restKey.trim()) {
      setError('La clé REST est requise pour l\u2019envoi serveur (notifications application fermée).');
      setSaving(false);
      return;
    }
    try {
      const { error: rpcError } = await supabase.rpc('save_zifek_onesignal', {
        os_enabled: enabled,
        os_app_id: appId.trim(),
        os_rest_key: restKey.trim(),
        os_safari_id: safariWebId.trim(),
      });
      if (rpcError) throw rpcError;
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\u2019enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  /** Vérifie réellement la clé REST auprès d'OneSignal (côté serveur). */
  const testKey = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('send-push', {
        body: { action: 'onesignal-status' },
      });
      if (invokeErr) throw invokeErr;
      const d = data as { configured?: boolean; valid?: boolean; enabled?: boolean; reason?: string } | null;
      if (!d?.configured) {
        setTestResult({
          ok: false,
          msg: d?.reason === 'no_rest_key' ? 'Clé REST manquante.' : 'App ID manquant.',
        });
      } else if (d.valid) {
        setTestResult({ ok: true, msg: 'Clé REST valide — l\u2019envoi serveur fonctionne.' });
      } else {
        setTestResult({ ok: false, msg: `Clé refusée par OneSignal (${d.reason || 'inconnu'}).` });
      }
    } catch {
      setTestResult({ ok: false, msg: 'Test impossible (fonction serveur injoignable).' });
    } finally {
      setTesting(false);
    }
  };

  const configured = !!appId.trim();
  const hasRestKey = !!restKey.trim();
  const statusLabel = loading ? '…' : enabled ? 'Activé' : 'Désactivé';

  return (
    <section className="bg-background-50 border border-background-200/70 rounded-lg p-5">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-notification-3-line text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-sm font-semibold text-foreground-900">OneSignal — Notifications push</h3>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              enabled ? 'bg-green-100 text-green-700' : 'bg-background-100 text-foreground-500'
            }`}>
              {statusLabel}
            </span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              configured ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'
            }`}>
              {configured ? 'App ID OK' : 'App ID manquant'}
            </span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
              hasRestKey ? 'bg-accent-100 text-accent-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {hasRestKey ? 'Envoi serveur prêt' : 'Clé REST manquante'}
            </span>
          </div>
          <p className="text-xs text-foreground-500 mb-4">
            Diffusion des notifications push en arrière-plan (application fermée), en complément du
            système de notifications interne. Récupérez ces identifiants dans votre tableau de bord OneSignal.
          </p>

          {/* Activer / Désactiver */}
          <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-background-100 mb-4">
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground-800">Activer OneSignal</p>
              <p className="text-[11px] text-foreground-500">Charge le SDK et envoie les notifications push aux abonnés.</p>
            </div>
            <button
              type="button"
              onClick={() => setEnabled((v) => !v)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors cursor-pointer flex-shrink-0 ${
                enabled ? 'bg-accent-500' : 'bg-background-200'
              }`}
              aria-label={enabled ? 'Désactiver' : 'Activer'}
            >
              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Champs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-foreground-600 mb-1">App ID</label>
              <input
                type="text"
                value={appId}
                onChange={(e) => setAppId(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 font-mono"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-foreground-600 mb-1">REST API Key</label>
              <div className="relative">
                <input
                  type={showKey ? 'text' : 'password'}
                  value={restKey}
                  onChange={(e) => setRestKey(e.target.value)}
                  placeholder="os_v2_app_..."
                  className="w-full h-10 px-3 pr-10 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 font-mono"
                />
                <button
                  onClick={() => setShowKey((v) => !v)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                  title={showKey ? 'Cacher' : 'Afficher'}
                >
                  <i className={`text-sm ${showKey ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                </button>
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[11px] font-semibold text-foreground-600 mb-1">
                Safari Web ID <span className="font-normal text-foreground-400">(optionnel — requis pour Safari)</span>
              </label>
              <input
                type="text"
                value={safariWebId}
                onChange={(e) => setSafariWebId(e.target.value)}
                placeholder="web.onesignal.auto.xxxxxxxx"
                className="w-full h-10 px-3 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 font-mono"
              />
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-600 mt-3 flex items-center gap-1.5">
              <i className="ri-error-warning-line"></i>{error}
            </p>
          )}

          <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
            <p className="text-[10px] text-foreground-400 flex items-center gap-1.5 max-w-md">
              <i className="ri-shield-check-line text-accent-500"></i>
              La REST API Key reste stockée côté plateforme et n&apos;est jamais exposée à l&apos;utilisateur final.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={testKey}
                disabled={testing}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer bg-background-100 text-foreground-700 hover:bg-background-200/70 transition-colors disabled:opacity-50"
              >
                {testing ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Test…</>
                ) : (
                  <><i className="ri-flask-line"></i>Tester la clé</>
                )}
              </button>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer bg-foreground-950 text-background-50 hover:bg-foreground-800 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin"></i>Enregistrement…</>
              ) : saved ? (
                <><i className="ri-check-line"></i>Enregistré</>
              ) : (
                <><i className="ri-save-line"></i>Enregistrer</>
              )}
            </button>
            </div>
          </div>

          {testResult && (
            <p className={`text-xs mt-3 flex items-center gap-1.5 ${testResult.ok ? 'text-green-600' : 'text-red-600'}`}>
              <i className={testResult.ok ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'}></i>
              {testResult.msg}
            </p>
          )}
          {!enabled && hasRestKey && (
            <p className="text-[11px] text-amber-600 mt-2 flex items-center gap-1.5">
              <i className="ri-information-line"></i>
              OneSignal est désactivé : l&apos;envoi serveur ne partira pas tant que l&apos;interrupteur ci-dessus reste off.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}